import SoundsResource from 'flow/api/sounds';
import Actions from 'flow/actions';

import {
    networkErrorDispatcher,
    httpGetErrorDispatcher,
    setSnackbar,
} from 'flow/actions/app-actions';

/**
 * Marker the API returns in the body of a 400 when a sound cannot be deleted
 * because something still references it. Not a user-facing string.
 */
const SOUND_IN_USE = 'In-Use';

const parseErrorBody = (request) => {
    try {
        return JSON.parse(request?.responseText ?? '');
    } catch {
        return null;
    }
};

export const fetchUserSounds = () => (dispatch) => {
    SoundsResource.getSounds(
        {
            onResult(json) {
                dispatch(receiveUserSounds(json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        true,
        true,
    );
};

export const createNewSound = (soundDTO, file) => (dispatch) => {
    SoundsResource.createSound(
        {
            onResult(json) {
                dispatch(uploadSound(json.key, file));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        soundDTO,
    );
};

const uploadSound = (entityKey, file) => (dispatch) => {
    SoundsResource.uploadSound(
        {
            onResult(json) {
                dispatch(createdSound(json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        entityKey,
        file,
    );
};

export const editSound = (soundDTO, usersoundKey) => (dispatch) => {
    SoundsResource.updateSound(
        {
            onResult(json) {
                dispatch(recieveEditedSound(usersoundKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        usersoundKey,
        soundDTO,
    );
};

export const callAndRecordSound =
    (name, description, callMobile, privateSound = false) =>
    (dispatch) => {
        const newSoundDTO = {
            name,
            description,
            privateSound,
        };

        SoundsResource.createSound(
            {
                onResult(json) {
                    dispatch(recordAndAddSound(json.key, callMobile));
                },
                onError: networkErrorDispatcher(dispatch),
            },
            newSoundDTO,
        );
    };

const recordAndAddSound = (soundEntityKey, callMobile) => (dispatch) => {
    SoundsResource.recordSound(
        {
            onResult(/**/) {},
            onError: networkErrorDispatcher(dispatch),
        },
        soundEntityKey,
        callMobile,
    );
};

/**
 * Deletes a sound. The API rejects the delete with a 400 when the sound is still
 * referenced by a queue, IVR, profile or voicemail greeting.
 *
 * @param usersoundKey the sound to delete
 * @param onInUse optional callback receiving the usageMap ({VOICEMAIL: ["Jane Doe"]})
 *        so the caller can show the usages. Falls back to a snackbar when omitted,
 *        so no caller can fail silently.
 */
export const deleteSound =
    (usersoundKey, { onInUse } = {}) =>
    (dispatch) => {
        SoundsResource.deleteSound(
            {
                onResult(/**/) {
                    dispatch(receiveDeleteSound(usersoundKey));
                },

                // 4xx is routed here, not to onError. See home-client.js.
                onClientError(request) {
                    const body = parseErrorBody(request);

                    if (body?.message === SOUND_IN_USE) {
                        if (onInUse) {
                            onInUse(body.usageMap ?? {});
                        } else {
                            dispatch(
                                setSnackbar({
                                    i18nextMessage: 'sound.delete.in_use',
                                    hasCloseButton: true,
                                }),
                            );
                        }
                        return;
                    }

                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'sound.delete.failed',
                            hasCloseButton: true,
                        }),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            usersoundKey,
        );
    };

export const fetchDefaultSounds = () => (dispatch) => {
    SoundsResource.getDefaultSounds({
        onResult(json) {
            dispatch(receiveDefaultSounds(json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

export const playSound = (soundId) => ({
    type: Actions.PLAY_SOUND,
    data: soundId,
});

const receiveUserSounds = (json) => ({
    type: Actions.RESULT_USER_SOUNDS,
    data: json,
});

const createdSound = (soundDTO) => ({
    type: Actions.RESULT_CREATE_NEW_SOUND,
    data: soundDTO,
});

const recieveEditedSound = (usersoundKey, json) => ({
    type: Actions.RESULT_EDIT_SOUND,
    key: usersoundKey,
    data: json,
});

const receiveDeleteSound = (usersoundKey) => ({
    type: Actions.RESULT_REMOVE_SOUND,
    key: usersoundKey,
});

const receiveDefaultSounds = (defaultSounds) => ({
    type: Actions.RESULT_DEFAULT_SOUNDS,
    data: defaultSounds,
});
